<p-toast></p-toast>
<p-progressBar mode="indeterminate" *ngIf="isLoading"></p-progressBar>

<app-page-shell>
  @if (request) {
    <app-page-detail-hero [auditStamp]="request"
      icon="pi pi-inbox"
      eyebrow="Warehouse Reservation"
      [title]="request.referenceNumber || ('Request #' + (request.id || request.referenceLineId))"
      [subtitle]="itemIdentityLabel(request, 'Operational reservation request')">
      <div tags class="flex flex-wrap gap-2">
        @if (request.deletedAt) { <p-tag value="DELETED" severity="danger"></p-tag> }
        <p-tag [value]="request.reservationStatus || 'PLANNED'" [severity]="reservationSeverity(request.reservationStatus)"></p-tag>
        <span>{{ request.warehouseName || '-' }}</span>
        <span>{{ request.conditionName || 'Any condition' }}</span>
        @if (itemSizeLabel()) { <span>{{ itemSizeLabel() }}</span> }
        <span>Reservation time {{ displayDateTime(reservationTime()) }}</span>
        <span>Reserve by {{ request.requiredBy ? displayDate(request.requiredBy) : 'No deadline' }}</span>
      </div>
      <div actions class="flex flex-wrap gap-2">
        @if (canViewOrigin()) {
          <p-button label="View in POSM" icon="pi pi-external-link" severity="secondary" [outlined]="true"
            [routerLink]="['/posm/projects', request.referenceHeaderId]" [queryParams]="{ tab: 'materials' }"></p-button>
        }
        <p-button label="Refresh Units" icon="pi pi-refresh" severity="secondary" [outlined]="true"
          [disabled]="!!request.deletedAt || (request.remainingQuantity || 0) <= 0"
          [loading]="isLoadingCandidates"
          (click)="refreshCandidates(true)"></p-button>
        <p-button label="Refresh Request" icon="pi pi-sync" severity="secondary" [outlined]="true" (click)="loadRequest()"></p-button>
        <p-button label="Back" icon="pi pi-arrow-left" severity="secondary" [outlined]="true"
          routerLink="/warehousing/reservations" [queryParams]="returnToListQueryParams"></p-button>
      </div>
    </app-page-detail-hero>

    <app-page-metric-strip [metrics]="metrics()"></app-page-metric-strip>

    <div class="warehousing-detail-grid">
      <app-page-form-section icon="pi pi-box" title="Request Context" subtitle="Source document, requested item, and physical size details.">
        <div class="warehousing-detail-list">
          <div><span>Reference Type</span><strong>{{ referenceTypeLabel() }}</strong></div>
          <div><span>Reference Name</span><strong>{{ request.referenceName || '-' }}</strong></div>
          <div><span>Item</span><strong>{{ request.itemName || '-' }}</strong></div>
          <div><span>Item Code</span><strong>{{ request.itemNumber || request.skuNumber || '-' }}</strong></div>
          @if (itemSizeLabel()) { <div><span>Item Size</span><strong>{{ itemSizeLabel() }}</strong></div> }
          <div><span>Weight</span><strong>{{ itemWeightLabel() }}</strong></div>
          <div><span>Storage Volume</span><strong>{{ itemVolumeLabel() }}</strong></div>
          <div><span>Request Id</span><strong>{{ request.id || '-' }}</strong></div>
          <div><span>Reference Line</span><strong>{{ request.referenceLineId || '-' }}</strong></div>
        </div>
      </app-page-form-section>

      <app-page-form-section icon="pi pi-warehouse" title="Allocation Criteria" subtitle="Warehouse, condition, deadline, and internal instructions.">
        <div class="warehousing-detail-list">
          <div><span>Warehouse</span><strong>{{ request.warehouseName || '-' }}</strong></div>
          <div><span>Condition</span><strong>{{ request.conditionName || 'Any condition' }}</strong></div>
          <div><span>{{ reservationTimeLabel() }}</span><strong>{{ displayDateTime(reservationTime()) }}</strong></div>
          <div><span>Reserve By</span><strong>{{ request.requiredBy ? displayDate(request.requiredBy) : 'No deadline' }}</strong></div>
          <div><span>Status</span><strong>{{ request.reservationStatus || 'PLANNED' }}</strong></div>
          <div class="full-width"><span>Notes</span><strong>{{ request.notes || 'No notes provided.' }}</strong></div>
        </div>
      </app-page-form-section>
    </div>

    <app-page-data-panel title="Reserved Stock Units" subtitle="Each allocation stays tied to this request line until released or consumed downstream." [resultText]="(request.allocations?.length || 0) + ' allocations'">
      <p-table [value]="request.allocations || []" styleClass="p-datatable-sm p-datatable-gridlines warehousing-data-table" [tableStyle]="{ 'min-width': '72rem' }">
        <ng-template pTemplate="header">
          <tr>
            <th>Stock Unit</th>
            <th>Warehouse / Zone</th>
            <th>Condition</th>
            <th class="text-right">Quantity</th>
            <th>Allocated</th>
            <th class="text-right">Action</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-allocation>
          <tr>
            <td>
              <div class="stack-cell">
                <span class="warehousing-code-pill">{{ allocation.stockUnitCode || ('Stock Unit #' + allocation.stockUnitId) }}</span>
                <div class="cell-meta vertical">
                  <span><i class="pi pi-hashtag"></i> {{ allocation.stockUnitId }}</span>
                  <span><i class="pi pi-file"></i> {{ allocation.notes || 'No notes' }}</span>
                </div>
              </div>
            </td>
            <td>
              <div class="stack-cell">
                <span class="cell-title">{{ allocation.warehouseName || request.warehouseName || '-' }}</span>
                <div class="cell-meta vertical">
                  <span><i class="pi pi-th-large"></i> {{ allocation.zoneName || (allocation.zoneId ? ('Zone #' + allocation.zoneId) : '-') }}</span>
                </div>
              </div>
            </td>
            <td>{{ allocation.conditionName || request.conditionName || 'Any condition' }}</td>
            <td class="text-right">{{ allocation.quantity | number:'1.0-4' }}</td>
            <td>{{ displayDateTime(allocation.createdAt || allocation.updatedAt) }}</td>
            <td class="text-right">
              <p-button icon="pi pi-times" label="Release" severity="danger" [outlined]="true" [disabled]="!!request.deletedAt"
                [loading]="isSaving('release:' + allocation.id)"
                (click)="releaseReservation(allocation.id)"></p-button>
            </td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
          <tr>
            <td colspan="6">
              <div class="warehousing-empty-state compact">
                <i class="pi pi-inbox"></i>
                <strong>No reserved stock units</strong>
                <span>This request has not been allocated yet.</span>
              </div>
            </td>
          </tr>
        </ng-template>
      </p-table>
    </app-page-data-panel>

    <app-page-data-panel title="Available Stock Units" subtitle="Reserve exact available units from the requested warehouse and condition." [resultText]="candidateUnits.length + ' candidate units'">
      <div class="panel-action-row">
        <span class="panel-action-copy">{{ request.deletedAt ? 'Deleted request · read only' : candidateCoverageText() }}</span>
        <div class="panel-action-buttons">
          <p-button icon="pi pi-refresh" label="Load Units" severity="secondary" [outlined]="true"
            [disabled]="isBulkReserving || (request.remainingQuantity || 0) <= 0"
            [loading]="isLoadingCandidates"
            (click)="refreshCandidates(true)"></p-button>
          <p-button icon="pi pi-check-circle" label="Reserve All" severity="success"
            [outlined]="true"
            [disabled]="!canReserveAllLoadedUnits()"
            [loading]="isBulkReserving"
            (click)="reserveAllUnits()"></p-button>
        </div>
      </div>

      @if (candidateUnits.length) {
        <p-table [value]="candidateUnits" styleClass="p-datatable-sm p-datatable-gridlines warehousing-data-table" [tableStyle]="{ 'min-width': '76rem' }">
          <ng-template pTemplate="header">
            <tr>
              <th>Stock Unit</th>
              <th>Location</th>
              <th>Condition</th>
              <th>Received</th>
              <th class="text-right">Available</th>
              <th>Reserve Qty</th>
              <th class="text-right">Action</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-unit>
            <tr>
              <td>
                <div class="stack-cell">
                  <span class="warehousing-code-pill">{{ unit.unitCode || ('Stock Unit #' + unit.id) }}</span>
                  <div class="cell-meta vertical">
                    <span><i class="pi pi-hashtag"></i> {{ unit.id }}</span>
                    <span><i class="pi pi-box"></i> {{ unit.itemNumber || request.itemNumber || '-' }}</span>
                    @if (itemSizeLabel()) { <span><i class="pi pi-expand"></i> {{ itemSizeLabel() }}</span> }
                  </div>
                </div>
              </td>
              <td>
                <div class="stack-cell">
                  <span class="cell-title">{{ request.warehouseName || '-' }}</span>
                  <div class="cell-meta vertical">
                    <span><i class="pi pi-th-large"></i> {{ unit.zoneId ? ('Zone #' + unit.zoneId) : '-' }}</span>
                  </div>
                </div>
              </td>
              <td>{{ request.conditionName || (unit.conditionId ? ('Condition #' + unit.conditionId) : 'Any condition') }}</td>
              <td>{{ displayDate(unit.receivedAt) }}</td>
              <td class="text-right">{{ unit.availableQuantity | number:'1.0-4' }}</td>
              <td>
                <p-inputNumber
                  [ngModel]="draftQuantity(unit)"
                  (ngModelChange)="setDraftQuantity(unit.id, $event)"
                  [min]="0"
                  [max]="maxReservableQuantity(unit)"
                  inputStyleClass="w-full">
                </p-inputNumber>
              </td>
              <td class="text-right">
                <p-button label="Reserve" icon="pi pi-check"
                  [disabled]="!!request.deletedAt || isBulkReserving"
                  [loading]="isSaving('reserve:' + unit.id)"
                  (click)="reserveUnit(unit)"></p-button>
              </td>
            </tr>
          </ng-template>
        </p-table>
      } @else if (isLoadingCandidates) {
        <div class="warehousing-empty-state compact">
          <i class="pi pi-spin pi-spinner"></i>
          <strong>Loading candidate stock units</strong>
          <span>Checking exact available stock in the requested warehouse.</span>
        </div>
      } @else if ((request.remainingQuantity || 0) <= 0) {
        <div class="warehousing-empty-state compact">
          <i class="pi pi-check-circle"></i>
          <strong>Request fully reserved</strong>
          <span>No additional stock units are required for this request.</span>
        </div>
      } @else {
        <div class="warehousing-empty-state compact">
          <i class="pi pi-search"></i>
          <strong>No candidate stock loaded</strong>
          <span>Load units to inspect available stock and reserve exact quantities.</span>
        </div>
      }
    </app-page-data-panel>
  } @else if (!isLoading) {
    <div class="warehousing-empty-detail">
      <i class="pi pi-search"></i>
      <strong>Reservation request not found</strong>
      <span>The selected reservation could not be loaded.</span>
    </div>
  }
</app-page-shell>
